import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, MapPin, Plus } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { BaitPicker, type BaitSelection } from "../components/BaitPicker";
import LiveSessionPage from "./LiveSession";

type FeedLine = { bait: BaitSelection; amount: string; unit: string };
type RodRef = { anglerId: number; rod: number; label: string };

const emptyBait = (): BaitSelection => ({
  canonical_id: null,
  user_bait_id: null,
  snapshot: "",
});

const FEED_METHODS = [
  "Bait boat",
  "Spomb / spod",
  "PVA bag",
  "PVA stick",
  "Catapult",
  "Throwing stick",
  "Hand fed",
  "Single hookbait / no loose feed",
  "Other",
];
const FEED_UNITS = ["grams", "kilograms", "boilies", "handfuls", "PVA bags", "stringers"];

function inferFeedMethod(value: unknown) {
  const text = String(value ?? "").toLowerCase();
  if (text.includes("bait boat")) return "Bait boat";
  if (text.includes("spomb") || text.includes("spod")) return "Spomb / spod";
  if (text.includes("pva bag") || text.includes("solid bag")) return "PVA bag";
  if (text.includes("pva stick") || text.includes("stick mix")) return "PVA stick";
  if (text.includes("catapult")) return "Catapult";
  if (text.includes("throwing stick")) return "Throwing stick";
  if (text.includes("hand feed") || text.includes("hand-fed")) return "Hand fed";
  if (text.includes("single hookbait") || text.includes("single bait")) return "Single hookbait / no loose feed";
  return "";
}

function rodKey(ref: RodRef) {
  return `${ref.anglerId}:${ref.rod}`;
}

export default function LiveSessionWithDeploymentPage() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ["carpintel_session", id],
    queryFn: () => fetch(`/app-api/sessions/${id}`).then((r) => {
      if (!r.ok) throw new Error("Session not found");
      return r.json();
    }),
    enabled: Boolean(id),
  });
  const anglersQuery = useQuery({
    queryKey: ["carpintel_session_anglers", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/anglers`).then((r) => (r.ok ? r.json() : [])),
    enabled: Boolean(id),
  });
  const stateQuery = useQuery({
    queryKey: ["bankside_rod_state", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/rod-state`).then((r) => (r.ok ? r.json() : { states: [], feed_events: [], positions: [] })),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });

  const session = sessionQuery.data;
  const anglers: any[] = anglersQuery.data ?? [];
  const positions: any[] = stateQuery.data?.positions ?? [];

  const adviceId = session?.advisor_recommendation_id;
  const adviceQuery = useQuery({
    queryKey: ["carpintel_advisor_recommendation", adviceId],
    queryFn: () => fetch(`/app-api/advisor/recommendations/${adviceId}`).then((r) => (r.ok ? r.json() : null)),
    enabled: Boolean(adviceId),
  });
  const advice = adviceQuery.data;

  const spotsQuery = useQuery({
    queryKey: ["carpintel_saved_spots", session?.lake_id],
    queryFn: () => session?.lake_id
      ? fetch(`/app-api/lakes/${session.lake_id}/spots`).then((r) => (r.ok ? r.json() : []))
      : Promise.resolve([]),
    enabled: Boolean(session?.lake_id),
  });
  const spots: any[] = spotsQuery.data ?? [];

  const rodRefs: RodRef[] = useMemo(
    () => anglers.flatMap((angler) =>
      Array.from({ length: Math.max(1, Number(angler.rods_count || 1)) }, (_, index) => ({
        anglerId: Number(angler.id),
        rod: index + 1,
        label: anglers.length > 1 ? `${angler.display_name} · Rod ${index + 1}` : `Rod ${index + 1}`,
      })),
    ),
    [anglers],
  );

  const positionMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const row of positions) map.set(`${Number(row.session_angler_id)}:${Number(row.rod_number)}`, row);
    return map;
  }, [positions]);

  const completeRefs = rodRefs.filter((ref) => Boolean(String(positionMap.get(rodKey(ref))?.spot_snapshot ?? "").trim()));
  const pendingRefs = rodRefs.filter((ref) => {
    const position = positionMap.get(rodKey(ref));
    return Boolean(position) && !String(position?.spot_snapshot ?? "").trim();
  });
  const readyRefs = rodRefs.filter((ref) => !positionMap.has(rodKey(ref)));

  const [deferredKeys, setDeferredKeys] = useState<string[]>([]);
  const [deployKey, setDeployKey] = useState("");
  const [positionSpot, setPositionSpot] = useState("");
  const [positionText, setPositionText] = useState("");
  const [feedMethod, setFeedMethod] = useState("");
  const [feedLines, setFeedLines] = useState<FeedLine[]>([{ bait: emptyBait(), amount: "", unit: "grams" }]);
  const [liquidLines, setLiquidLines] = useState<FeedLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const deployRef = rodRefs.find((ref) => rodKey(ref) === deployKey) ?? null;
  const promptRef = pendingRefs[0] ?? readyRefs.find((ref) => !deferredKeys.includes(rodKey(ref))) ?? null;
  const allDeployed = rodRefs.length > 0 && completeRefs.length === rodRefs.length && !pendingRefs.length && !readyRefs.length;

  function primeDeploymentForm(ref: RodRef) {
    setPositionSpot("");
    setPositionText(ref.rod === 1 ? String(advice?.recommended_area ?? "") : "");
    setFeedMethod(inferFeedMethod(advice?.recommended_bait_approach));
    setFeedLines([{
      bait: { ...emptyBait(), snapshot: String(advice?.recommended_main_bait ?? "") },
      amount: "",
      unit: "grams",
    }]);
    setLiquidLines(
      advice?.recommended_additive
        ? [{ bait: { ...emptyBait(), snapshot: String(advice.recommended_additive), canonical_id: null, user_bait_id: null }, amount: "", unit: "ml" }]
        : [],
    );
  }

  async function refreshDeployment() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["bankside_rod_state", id] }),
      queryClient.invalidateQueries({ queryKey: ["carpintel_session_rod_setups", id] }),
      queryClient.invalidateQueries({ queryKey: ["carpintel_session_events", id] }),
    ]);
  }

  async function beginCast(ref: RodRef) {
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/deployment-start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_angler_id: ref.anglerId, rod_number: ref.rod }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not start this cast");
      if (data.already_deployed) {
        await refreshDeployment();
        setMessage(`${ref.label} is already fishing.`);
        return;
      }
      primeDeploymentForm(ref);
      setDeployKey(rodKey(ref));
      setDeferredKeys((current) => current.filter((key) => key !== rodKey(ref)));
      await refreshDeployment();
      setMessage(`${ref.label} clock started. Add the position and initial feed while you put it out.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not start this cast");
    } finally {
      setBusy(false);
    }
  }

  function deferRod(ref: RodRef) {
    setDeferredKeys((current) => current.includes(rodKey(ref)) ? current : [...current, rodKey(ref)]);
    setMessage(`${ref.label} left as not fishing yet. It will stay ready to cast in the live session.`);
  }

  async function finishCast() {
    if (!deployRef) return;
    const selected = spots.find((spot) => String(spot.id) === positionSpot);
    const snapshot = selected?.name || positionText.trim();
    if (!snapshot) return setMessage("Choose or describe where this rod is fishing.");
    if (!feedMethod) return setMessage("Choose how you are feeding this rod or spot.");

    const food = feedLines
      .filter((line) => line.bait.snapshot.trim())
      .map((line) => ({
        ...line.bait,
        amount: line.amount ? Number(line.amount) : null,
        unit: line.unit,
        feed_method: feedMethod,
        role: "feed",
      }));
    const liquids = liquidLines
      .filter((line) => line.bait.snapshot.trim())
      .map((line) => ({
        ...line.bait,
        amount: line.amount ? Number(line.amount) : null,
        unit: "ml",
        feed_method: feedMethod,
        role: "additive",
      }));
    if (feedMethod !== "Single hookbait / no loose feed" && !food.length) {
      return setMessage("Add at least one loose-feed bait, or choose single hookbait / no loose feed.");
    }

    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/deployment-finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_angler_id: deployRef.anglerId,
          rod_number: deployRef.rod,
          spot_id: selected?.id ?? null,
          spot_snapshot: snapshot,
          distance_m: selected?.distance_m ?? null,
          depth_ft: selected?.depth_ft ?? null,
          feed_method: feedMethod,
          mix: feedMethod === "Single hookbait / no loose feed" ? [] : [...food, ...liquids],
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not finish this cast");
      const label = deployRef.label;
      setDeployKey("");
      await refreshDeployment();
      setMessage(`${label} is fishing. Position, feed and tackle exposure all start from the Cast tap.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not finish this cast");
    } finally {
      setBusy(false);
    }
  }

  if (sessionQuery.isLoading || anglersQuery.isLoading || stateQuery.isLoading) {
    return <div className="mx-auto max-w-2xl px-4 py-10 text-sm text-secondary">Opening live session…</div>;
  }

  return (
    <>
      <div className="mx-auto w-full max-w-[760px] space-y-3 px-4 pt-3">
        {rodRefs.length ? (
          allDeployed && !deployRef ? (
            <section className="rounded-xl border border-border bg-raised px-3 py-2.5" aria-label="Rod deployment status">
              <div className="flex items-center justify-between gap-3">
                <p className="flex min-w-0 items-center gap-2 text-sm font-semibold text-primary">
                  <Check className="size-4 shrink-0 text-accent" />
                  <span>{completeRefs.length} of {rodRefs.length} rods fishing</span>
                </p>
                <span className="shrink-0 rounded-full bg-accent/10 px-2.5 py-1 text-xs font-semibold text-accent">All deployed</span>
              </div>
            </section>
          ) : (
            <section className="rounded-2xl border border-accent/40 bg-accent/5 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">Deploy your rods</p>
                  <h2 className="mt-1 text-lg font-bold text-primary">{completeRefs.length} of {rodRefs.length} rods fishing</h2>
                  <p className="mt-1 text-sm leading-5 text-secondary">Cast is the clock start. Position and initial feed are attached to that exact moment.</p>
                </div>
                {completeRefs.length === rodRefs.length ? <span className="rounded-full bg-inset px-3 py-1 text-xs font-semibold text-primary"><Check className="mr-1 inline size-3.5" /> All deployed</span> : null}
              </div>

              {promptRef ? (
                <div className="mt-4 rounded-xl border border-border bg-raised p-3">
                  <p className="font-semibold text-primary">{promptRef.label} {pendingRefs.includes(promptRef) ? "cast started" : "ready"}</p>
                  <p className="mt-1 text-sm text-secondary">{pendingRefs.includes(promptRef) ? "Finish the position and feed details for the cast already in progress." : `When ${promptRef.label} goes out, tap Cast first so its exposure starts immediately.`}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => beginCast(promptRef)} isLoading={busy}>{pendingRefs.includes(promptRef) ? `Finish ${promptRef.label} cast` : `Cast ${promptRef.label}`} <ChevronRight className="size-4" /></Button>
                    {!pendingRefs.includes(promptRef) ? <Button size="sm" variant="secondary" onClick={() => deferRod(promptRef)} disabled={busy}>Not fishing this rod yet</Button> : null}
                  </div>
                </div>
              ) : readyRefs.length ? (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-semibold text-primary">Not fishing yet</p>
                  {readyRefs.map((ref) => (
                    <div key={rodKey(ref)} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-raised px-3 py-2">
                      <div><p className="text-sm font-semibold text-primary">{ref.label}</p><p className="text-xs text-secondary">Ready to cast when you need it.</p></div>
                      <Button size="sm" variant="secondary" onClick={() => beginCast(ref)} disabled={busy}>Cast now</Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          )
        ) : null}

        {deployRef ? (
          <section className="rounded-2xl border border-accent/50 bg-raised p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-accent">Cast in progress</p>
                <h2 className="mt-1 text-lg font-bold text-primary">{deployRef.label}</h2>
                <p className="mt-1 text-sm text-secondary">The exposure clock is already running. Finish where it is fishing and what went in with it.</p>
              </div>
              <button type="button" className="min-h-11 px-2 text-sm text-secondary" onClick={() => setDeployKey("")}>Close</button>
            </div>

            <div className="mt-4 space-y-5">
              <div>
                <label className="text-sm font-semibold text-primary">Position / spot</label>
                <select className="mt-2 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary" value={positionSpot} onChange={(e) => { setPositionSpot(e.target.value); if (e.target.value) setPositionText(""); }}>
                  <option value="">Describe it myself</option>
                  {spots.map((spot) => <option key={spot.id} value={String(spot.id)}>{spot.name}</option>)}
                </select>
                {!positionSpot ? <Input className="mt-2" value={positionText} onChange={(e) => setPositionText(e.target.value)} placeholder="e.g. left margin, island channel, lilies" /> : null}
              </div>

              <div>
                <p className="text-sm font-semibold text-primary">How are you feeding this rod / spot?</p>
                <div className="mt-2 grid grid-cols-2 gap-2">{FEED_METHODS.map((method) => <button key={method} type="button" onClick={() => setFeedMethod(method)} className={`min-h-11 rounded-lg border px-2 py-2 text-xs font-semibold ${feedMethod === method ? "border-accent bg-accent text-white" : "border-border bg-background text-primary"}`}>{method}</button>)}</div>
              </div>

              {feedMethod && feedMethod !== "Single hookbait / no loose feed" ? (
                <>
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-primary">Initial loose feed</p>
                    {feedLines.map((line, index) => (
                      <div key={index} className="rounded-xl border border-border bg-background p-3">
                        <BaitPicker id={`deploy-feed-${index}`} label={index === 0 ? "Feed bait" : `Feed bait ${index + 1}`} value={line.bait} onChange={(bait) => setFeedLines((current) => current.map((row, i) => i === index ? { ...row, bait } : row))} speciesFilter={session?.target_species} presentationRole="feed" />
                        <div className="mt-2 grid grid-cols-2 gap-2"><Input type="number" min="0" step="0.1" placeholder="Amount" value={line.amount} onChange={(e) => setFeedLines((current) => current.map((row, i) => i === index ? { ...row, amount: e.target.value } : row))} /><select className="h-11 rounded-lg border border-border bg-background px-2 text-sm text-primary" value={line.unit} onChange={(e) => setFeedLines((current) => current.map((row, i) => i === index ? { ...row, unit: e.target.value } : row))}>{FEED_UNITS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}</select></div>
                        {feedLines.length > 1 ? <button type="button" className="mt-2 min-h-10 text-xs font-semibold text-error" onClick={() => setFeedLines((current) => current.filter((_, i) => i !== index))}>Remove feed item</button> : null}
                      </div>
                    ))}
                    <Button type="button" variant="tertiary" size="sm" onClick={() => setFeedLines((current) => [...current, { bait: emptyBait(), amount: "", unit: "grams" }])}><Plus className="size-4" /> Add another feed bait</Button>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-primary">Liquids / additives</p>
                    {liquidLines.map((line, index) => (
                      <div key={index} className="rounded-xl border border-border bg-background p-3">
                        <BaitPicker id={`deploy-liquid-${index}`} label={`Liquid / additive ${index + 1}`} value={line.bait} onChange={(bait) => setLiquidLines((current) => current.map((row, i) => i === index ? { ...row, bait } : row))} speciesFilter={session?.target_species} presentationRole="additive" />
                        <Input className="mt-2" type="number" min="0" step="0.1" placeholder="Amount (ml)" value={line.amount} onChange={(e) => setLiquidLines((current) => current.map((row, i) => i === index ? { ...row, amount: e.target.value } : row))} />
                        <button type="button" className="mt-2 min-h-10 text-xs font-semibold text-error" onClick={() => setLiquidLines((current) => current.filter((_, i) => i !== index))}>Remove liquid</button>
                      </div>
                    ))}
                    <Button type="button" variant="tertiary" size="sm" onClick={() => setLiquidLines((current) => [...current, { bait: emptyBait(), amount: "", unit: "ml" }])}><Plus className="size-4" /> Add liquid / additive</Button>
                  </div>
                </>
              ) : null}

              <p className="flex items-start gap-2 text-xs leading-5 text-secondary"><MapPin className="mt-0.5 size-4 shrink-0" /> Saving these details completes the same cast that started when you tapped Cast. It does not create a second rod move.</p>
              <Button className="w-full" onClick={finishCast} isLoading={busy}>Finish {deployRef.label} cast <Check /></Button>
            </div>
          </section>
        ) : null}

        {message && (!allDeployed || deployRef) ? <p className="rounded-lg border border-border bg-raised p-3 text-sm text-secondary" role="status">{message}</p> : null}
      </div>

      <LiveSessionPage />
    </>
  );
}
